// Active skills (Sword Master abilities).
//
// Skills cost mana and can't be recast while active. Levels are bought with gold during a
// run and reset on ascension. Each level's price is pegged to monster gold at a deeper stage,
// so stronger skill levels open up as a run pushes further.
export type SkillId = 'strike' | 'warcry' | 'frenzy' | 'midas' | 'firesword' | 'deadly';

export interface SkillDef {
  id: SkillId;
  name: string;
  icon: string;
  /** Stage (best ever) at which level 1 can be bought. */
  unlockStage: number;
  manaCost: number;
  durationSec: number; // 0 = instant
  maxLevel: number;
  /** Level n costs monsterGold(unlockStage + (n - 1) * stagesPerLevel) * goldMult. */
  stagesPerLevel: number;
  goldMult: number;
}

export const SKILLS: readonly SkillDef[] = [
  { id: 'strike',    name: 'Toy Bomb',        icon: '💣', unlockStage: 3,  manaCost: 20, durationSec: 0,  maxLevel: 25, stagesPerLevel: 18, goldMult: 20 },
  { id: 'warcry',    name: 'Rally',           icon: '🛡️', unlockStage: 8,  manaCost: 40, durationSec: 20, maxLevel: 25, stagesPerLevel: 20, goldMult: 25 },
  { id: 'frenzy',    name: 'Hero Time',       icon: '🌟', unlockStage: 15, manaCost: 50, durationSec: 15, maxLevel: 25, stagesPerLevel: 20, goldMult: 25 },
  { id: 'midas',     name: 'Piggy Bank',      icon: '🐷', unlockStage: 25, manaCost: 40, durationSec: 30, maxLevel: 25, stagesPerLevel: 22, goldMult: 25 },
  { id: 'firesword', name: 'Slash',           icon: '🗡️', unlockStage: 40, manaCost: 40, durationSec: 30, maxLevel: 25, stagesPerLevel: 22, goldMult: 30 },
  { id: 'deadly',    name: 'Lucky Star',      icon: '🍀', unlockStage: 60, manaCost: 45, durationSec: 25, maxLevel: 25, stagesPerLevel: 24, goldMult: 30 },
];

export const SKILL_BY_ID: ReadonlyMap<SkillId, SkillDef> = new Map(SKILLS.map((s) => [s.id, s]));

/** Per-level effect formulas (level >= 1). Artifact bonuses are applied on top in Game. */
export const SKILL_POWER = {
  strikeTaps: (lvl: number) => 40 + 20 * (lvl - 1),       // instant hit = N taps
  warcryMult: (lvl: number) => 1.5 + 0.5 * lvl,             // hero DPS multiplier
  frenzyTapsPerSec: (lvl: number) => 8 + 2 * lvl,           // auto taps per second
  midasMult: (lvl: number) => 1.5 + 0.75 * lvl,             // gold multiplier
  fireswordMult: (lvl: number) => 1.5 + 0.5 * lvl,          // tap damage multiplier
  deadlyCritChance: (lvl: number) => Math.min(0.5, 0.1 + 0.02 * lvl),
  deadlyCritMult: (lvl: number) => 1 + 0.5 * lvl,
} as const;

export const MANA = {
  base: 100,
  perSkillLevel: 5,   // max mana grows with total skill levels
  regenPerSec: 0.6,
} as const;

export function describeSkill(id: SkillId, lvl: number): string {
  const L = Math.max(1, lvl);
  const P = SKILL_POWER;
  switch (id) {
    case 'strike': return `Instantly deal ${P.strikeTaps(L)} taps of damage`;
    case 'warcry': return `Guardians deal x${P.warcryMult(L).toFixed(1)} damage`;
    case 'frenzy': return `Auto-tap ${P.frenzyTapsPerSec(L)} times per second`;
    case 'midas': return `x${P.midasMult(L).toFixed(2)} coins from enemies`;
    case 'firesword': return `Tap damage x${P.fireswordMult(L).toFixed(1)}`;
    case 'deadly': return `+${Math.round(P.deadlyCritChance(L) * 100)}% crit chance, x${P.deadlyCritMult(L).toFixed(1)} crit damage`;
  }
}
